import { InputTypesForFilter } from 'alurkerja-ui'
import { useUiFromSpecContext } from '../../../EditorContextProvider'
import { isEmpty } from 'lodash'
import { useEffect, useMemo } from 'react'
import { addRandomSuffix } from '../../utils/addRandomSuffix'
import {
  validateDateTime,
  validateDateOnly,
  getDateValidationErrorMessage,
  getConstraintDates,
  extractFieldName,
} from './utils/dateValidation'
import moment from 'moment'
import { ErrorBoundary } from '@sentry/react'

export const RenderInput = (props: any) => {
  const { item, form, inputSize } = props
  const { language, designState } = useUiFromSpecContext()

  // Extract field names from date validation to watch
  const fieldsToWatch = useMemo(() => {
    const fields: string[] = []

    if (item.dateValidation?.minDate?.fromField && typeof item.dateValidation.minDate.value === 'string') {
      const fieldName = extractFieldName(item.dateValidation.minDate.value)
      if (fieldName) fields.push(fieldName)
    }

    if (item.dateValidation?.maxDate?.fromField && typeof item.dateValidation.maxDate.value === 'string') {
      const fieldName = extractFieldName(item.dateValidation.maxDate.value)
      if (fieldName) fields.push(fieldName)
    }

    return fields
  }, [item.dateValidation])

  // Watch all extracted field names to ensure the component re-renders when they change
  const watchedValues = form.watch(fieldsToWatch)

  // Watch for changes in constraints for re-rendering
  const constraintsSnapshot = useMemo(() => {
    return JSON.stringify(item.constraints || {})
  }, [item.constraints])

  // Calculate constraint dates once without side effects
  const dateConstraints = useMemo(() => {
    return getConstraintDates(item.dateValidation, extractFieldName, form.watch)
  }, [item.dateValidation, JSON.stringify(watchedValues)])

  // Create props for the input component without triggering form actions
  const dateValidationProps = useMemo(() => {
    return {
      minDate: dateConstraints.minDate,
      maxDate: dateConstraints.maxDate,
    }
  }, [dateConstraints])

  useEffect(() => {
    const currentValue = form.getValues(item.name)
    // Only set value if current value doesn't exist and default value is provided
    if (
      (currentValue === undefined || currentValue === null || currentValue === '') &&
      item.defaultValue &&
      !isEmpty(item.defaultValue)
    ) {
      if (item.form_field_type === 'INPUT_SWITCH' || item.form_field_type === 'INPUT_TOOGLE') {
        // Set the default value only if current value is empty
        const booleanValue = item.defaultValue === 'true'
        form.setValue(item.name, booleanValue)
      } else if (item.form_field_type === 'INPUT_DATE' || item.form_field_type === 'INPUT_DATETIME-LOCAL') {
        // Handle date inputs
        const defaultValue =
          typeof item.defaultValue === 'string' && item.defaultValue
            ? moment(item.defaultValue).isValid()
              ? moment(item.defaultValue).toDate()
              : null
            : item.defaultValue || null
        form.setValue(item.name, defaultValue)
      } else {
        // Set the default value only if current value is empty
        form.setValue(item.name, item.defaultValue)
      }
    } else {
      if (item.form_field_type === 'INPUT_TOOGLE' || item.form_field_type === 'INPUT_SWITCH') {
        // Set the default value only if current value is empty
        const booleanValue = String(currentValue) === 'true'
        form.setValue(item.name, booleanValue)
      } else if (item.form_field_type === 'INPUT_DATE' || item.form_field_type === 'INPUT_DATETIME-LOCAL') {
        // Handle date inputs
        let defaultValue = null
        if (typeof currentValue === 'string' && currentValue) {
          const momentObj = moment(currentValue) // Auto-detect
          if (momentObj.isValid()) {
            defaultValue = momentObj.toDate()
          }
        } else if (currentValue) {
          defaultValue = currentValue
        }
        form.setValue(item.name, defaultValue)
      }
    }

    if (!item.name && isEmpty(item.name)) {
      item.name = addRandomSuffix('input') // Update the item with the generated name
    }
  }, [item.defaultValue, item.name, form])


  useEffect(() => {

    const currentValue = form.getValues(item.name)
    if (item.form_field_type === 'INPUT_DATE' || item.form_field_type === 'INPUT_DATETIME-LOCAL') {
      // Handle date inputs
      let defaultValue = null
      // somehow currentValue is array force to be date or string
      if (Array.isArray(currentValue)) {
        defaultValue = currentValue.length > 0 ? currentValue[0] : null
      }
      if (typeof currentValue === 'string' && currentValue) {
        const momentObj = moment(currentValue) // Auto-detect
        if (momentObj.isValid()) {
          defaultValue = momentObj.toDate()
        }
      } else if (currentValue) {
        defaultValue = currentValue
      }
      form.setValue(item.name, defaultValue)
    }
    return () => {

    }
  }, [item.defaultValue]);

  // Apply validation rules based on constraints
  useEffect(() => {
    if (item.name) {
      // Get current value before unregistering
      // const currentValue = form.getValues(item.name)

      // // Unregister the field first to avoid validation conflicts
      // form.unregister(item.name, { keepValue: true })

      // Build validation rules
      const validationRules: any = {
        required: item?.constraints?.required ? 'This field is required' : false,
        // value: currentValue || item?.defaultValue,
      }

      // Add custom date validation resolver
      if (item.dateValidation && (dateConstraints.minDate || dateConstraints.maxDate)) {
        validationRules.validate = {
          dateValidation: (value: any) => {
            if (!value) return true

            const date = new Date(value)
            let result

            if (item.form_field_type === 'INPUT_DATETIME-LOCAL') {
              result = validateDateTime(
                date,
                dateConstraints.minDate,
                dateConstraints.maxDate,
                !!item.dateValidation?.minDate?.fromField
              )
            } else {
              result = validateDateOnly(
                date,
                dateConstraints.minDate,
                dateConstraints.maxDate,
                !!item.dateValidation?.minDate?.fromField
              )
            }

            // Get source field label for better error messages
            let sourceFieldLabel = ''
            if (item.dateValidation?.minDate?.fromField && typeof item.dateValidation.minDate.value === 'string') {
              const fieldName = extractFieldName(item.dateValidation.minDate.value)
              if (fieldName) sourceFieldLabel = fieldName
            }

            return (
              result.isValid ||
              getDateValidationErrorMessage(
                item.form_field_type === 'INPUT_DATETIME-LOCAL',
                result.errorType,
                sourceFieldLabel
              )
            )
          },
        }
      }

      // Re-register with updated validation rules
      form.register(item.name, validationRules)
    }
  }, [item.name, constraintsSnapshot, JSON.stringify(dateConstraints), form])

  return (
    <ErrorBoundary fallback={(eror) => {
      console.error('Error rendering input component:', eror, item)
      return <div>Somthing Went Wrong Rendering This Input </div>
    }}>
      <InputTypesForFilter
        baseUrl={''}
        labelSize={designState.fontSize}
        labelFormat={designState.textFormat}
        labelAlign={designState.textAlign}
        labelColor={designState.textColor}
        fieldSpec={item}
        setValue={form.setValue}
        control={form.control}
        inputSize={inputSize}
        inline={false}
        className={item.className}
        language={language}
        {...dateValidationProps} // Pass the calculated minDate and maxDate props
      />
    </ErrorBoundary>
  )
}
